import { ClassValue } from 'clsx';
import { ReactNode, useCallback, useRef } from 'react';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { useCallbackOnEnter } from '@shared/util/hooks/useCallbackOnEnter';
import { i18n } from '@shared/util/i18n';
import { Text } from '../Text';
import { Dialog, DialogProps } from './Dialog';
import { useFitLongestLine } from './useFitLongestLine';
import styles from './ModalAlert.module.css';

export interface AlertProps extends Omit<DialogProps, 'title' | 'mode'> {
  onClose?: () => void | Promise<void>;
  title?: ReactNode;
  message?: ReactNode;
  children?: ReactNode;
  buttonLabel?: string;
  inline?: boolean;
  className?: ClassValue;
}

export function Alert({ inline = false, ...props }: AlertProps) {
  if (inline) {
    return <AlertContent {...props} />;
  } else {
    return <ModalAlert {...props} />;
  }
}

function ModalAlert({
  title,
  message,
  children,
  buttonLabel,
  isOpen = true,
  onClose,
  onOpenChange,
  className,
  lightDismiss,
  testID,
}: AlertProps) {
  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
      if (!open) {
        void onClose?.();
      }
    },
    [onClose, onOpenChange],
  );

  const handleClose = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  return (
    <Dialog
      mode="modal"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      lightDismiss={lightDismiss}
      testID={testID}
      className={[styles.alert, className]}
      animate
    >
      <AlertContent title={title} message={message} buttonLabel={buttonLabel} onClose={handleClose}>
        {children}
      </AlertContent>
    </Dialog>
  );
}

function AlertContent({
  title,
  message,
  children = message,
  buttonLabel = i18n('common.ok'),
  onClose,
}: AlertProps & { onClose?: () => void }) {
  useCallbackOnEnter({ callback: onClose, enabled: !!onClose });
  const messageRef = useRef<HTMLDivElement>(null);
  useFitLongestLine(messageRef, children);

  return (
    <View gap={3} className={styles.alertContent}>
      <View gap={1}>
        {title && (
          <Text
            size="h4"
            weight="semibold"
            numberOfLines={2}
            align="center"
            balance
            className={styles.title}
          >
            {title}
          </Text>
        )}
        <View ref={messageRef} className={styles.message}>
          <Text size="body1" align="left" pretty>
            {children}
          </Text>
        </View>
      </View>
      <View row gap={1} center>
        <Button variant="primary" size="large" label={buttonLabel} onClick={onClose} />
      </View>
    </View>
  );
}
